import { useReducer, useCallback } from 'react';
import type {
  MigrationStep,
  MigrationMode,
  PreviewResponse,
  ValidateResponse,
  ExecuteResponse,
} from '../types';

interface MigrationState {
  step: MigrationStep;
  sourceOrganisationId: string | null;
  sourceOrganisationName: string | null;
  targetOrganisationId: string | null;
  targetOrganisationName: string | null;
  mode: MigrationMode;
  archiveSource: boolean;
  previewData: PreviewResponse | null;
  validationData: ValidateResponse | null;
  executionResult: ExecuteResponse | null;
  batchId: string | null;
}

type MigrationAction =
  | { type: 'SELECT_SOURCE'; id: string; name: string }
  | { type: 'SELECT_TARGET'; id: string; name: string }
  | { type: 'NEXT_TO_MODE' }
  | { type: 'SELECT_MODE'; mode: MigrationMode }
  | { type: 'PREVIEW_SUCCESS'; data: PreviewResponse }
  | { type: 'VALIDATE_SUCCESS'; data: ValidateResponse }
  | { type: 'VALIDATE_FAILURE'; data: ValidateResponse }
  | { type: 'SET_ARCHIVE'; value: boolean }
  | { type: 'CONFIRM' }
  | { type: 'EXECUTE_START' }
  | { type: 'EXECUTE_SUCCESS'; result: ExecuteResponse }
  | { type: 'EXECUTE_FAILURE'; result: ExecuteResponse }
  | { type: 'BACK_TO_SELECT_ACCOUNTS' }
  | { type: 'BACK_TO_PREVIEW' }
  | { type: 'RESET' };

const initialState: MigrationState = {
  step: 'select-accounts',
  sourceOrganisationId: null,
  sourceOrganisationName: null,
  targetOrganisationId: null,
  targetOrganisationName: null,
  mode: 'move',
  archiveSource: false,
  previewData: null,
  validationData: null,
  executionResult: null,
  batchId: null,
};

function reducer(state: MigrationState, action: MigrationAction): MigrationState {
  switch (action.type) {
    case 'SELECT_SOURCE':
      return {
        ...state,
        sourceOrganisationId: action.id,
        sourceOrganisationName: action.name,
      };

    case 'SELECT_TARGET':
      return {
        ...state,
        targetOrganisationId: action.id,
        targetOrganisationName: action.name,
      };

    case 'NEXT_TO_MODE':
      if (
        !state.sourceOrganisationId ||
        !state.targetOrganisationId ||
        state.sourceOrganisationId === state.targetOrganisationId
      ) {
        return state;
      }
      return {
        ...state,
        step: 'select-mode',
      };

    case 'SELECT_MODE':
      return {
        ...state,
        mode: action.mode,
      };

    case 'PREVIEW_SUCCESS':
      if (state.step !== 'select-mode') return state;
      return {
        ...state,
        previewData: action.data,
        validationData: null,
        step: 'preview',
      };

    case 'VALIDATE_SUCCESS':
      if (state.step !== 'preview') return state;
      return {
        ...state,
        validationData: action.data,
      };

    case 'VALIDATE_FAILURE':
      if (state.step !== 'preview') return state;
      return {
        ...state,
        validationData: action.data,
      };

    case 'SET_ARCHIVE':
      return {
        ...state,
        archiveSource: action.value,
      };

    case 'CONFIRM':
      if (!state.validationData?.valid) return state;
      return {
        ...state,
        step: 'confirm',
      };

    case 'EXECUTE_START':
      return {
        ...state,
        step: 'executing',
        executionResult: null,
        batchId: null,
      };

    case 'EXECUTE_SUCCESS':
      return {
        ...state,
        step: 'success',
        executionResult: action.result,
        batchId: action.result.batchId ?? null,
      };

    case 'EXECUTE_FAILURE':
      return {
        ...state,
        step: 'error',
        executionResult: action.result,
        batchId: action.result.batchId ?? null,
      };

    case 'BACK_TO_SELECT_ACCOUNTS':
      return {
        ...state,
        step: 'select-accounts',
        mode: initialState.mode,
        previewData: null,
        validationData: null,
      };

    case 'BACK_TO_PREVIEW':
      return {
        ...state,
        step: 'preview',
        validationData: null,
        executionResult: null,
      };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

export function useMigrationState() {
  const [state, dispatch] = useReducer(reducer, initialState);

  const selectSource = useCallback((id: string, name: string) => {
    dispatch({ type: 'SELECT_SOURCE', id, name });
  }, []);

  const selectTarget = useCallback((id: string, name: string) => {
    dispatch({ type: 'SELECT_TARGET', id, name });
  }, []);

  const nextToMode = useCallback(() => {
    dispatch({ type: 'NEXT_TO_MODE' });
  }, []);

  const selectMode = useCallback((mode: MigrationMode) => {
    dispatch({ type: 'SELECT_MODE', mode });
  }, []);

  const previewSuccess = useCallback((data: PreviewResponse) => {
    dispatch({ type: 'PREVIEW_SUCCESS', data });
  }, []);

  const validateSuccess = useCallback((data: ValidateResponse) => {
    dispatch({ type: 'VALIDATE_SUCCESS', data });
  }, []);

  const validateFailure = useCallback((data: ValidateResponse) => {
    dispatch({ type: 'VALIDATE_FAILURE', data });
  }, []);

  const setArchive = useCallback((value: boolean) => {
    dispatch({ type: 'SET_ARCHIVE', value });
  }, []);

  const confirm = useCallback(() => {
    dispatch({ type: 'CONFIRM' });
  }, []);

  const executeStart = useCallback(() => {
    dispatch({ type: 'EXECUTE_START' });
  }, []);

  const executeSuccess = useCallback((result: ExecuteResponse) => {
    dispatch({ type: 'EXECUTE_SUCCESS', result });
  }, []);

  const executeFailure = useCallback((result: ExecuteResponse) => {
    dispatch({ type: 'EXECUTE_FAILURE', result });
  }, []);

  const backToSelectAccounts = useCallback(() => {
    dispatch({ type: 'BACK_TO_SELECT_ACCOUNTS' });
  }, []);

  const backToPreview = useCallback(() => {
    dispatch({ type: 'BACK_TO_PREVIEW' });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  return {
    state,
    actions: {
      selectSource,
      selectTarget,
      nextToMode,
      selectMode,
      previewSuccess,
      validateSuccess,
      validateFailure,
      setArchive,
      confirm,
      executeStart,
      executeSuccess,
      executeFailure,
      backToSelectAccounts,
      backToPreview,
      reset,
    },
  };
}
